import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFirebaseAuth, getFirebaseDb } from '../firebase';
import { User, signInWithEmailAndPassword, signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc, arrayUnion, getDoc, collection, addDoc } from 'firebase/firestore';
import { adminApi } from '../services/api';

type AdminRole = 'admin' | 'editor';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  role: AdminRole | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<AdminRole | null>(null);

  useEffect(() => {
    console.log('AuthProvider - Setting up auth state listener');    
    // Set up the auth state listener
    const unsubscribe = onAuthStateChanged(getFirebaseAuth(), async (user) => {
      console.log('AuthProvider - Auth state changed:', user);
      if (user) {
        // Get the Firebase ID token
        const token = await user.getIdToken();
        localStorage.setItem('token', token);
        
        // Fetch admin role from Firestore
        try {
          const adminDoc = await getDoc(doc(getFirebaseDb(), 'admins', user.uid));
          if (adminDoc.exists()) {
            const adminData = adminDoc.data();
            const userRole = adminData.role as AdminRole;
            setRole(userRole || 'editor');
            localStorage.setItem('userRole', userRole || 'editor');
          } else {
            setRole('editor');
            localStorage.setItem('userRole', 'editor');
          }
        } catch (error) {
          console.error('Error fetching admin role:', error);
          setRole('editor');
          localStorage.setItem('userRole', 'editor');
        }
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        setRole(null);
      }
      setUser(user);
      setLoading(false);
    }, (error) => {
      console.error('AuthProvider - Auth state error:', error);
      setLoading(false);
    });

    // Clean up the listener when the component unmounts
    return () => {
      console.log('AuthProvider - Cleaning up auth state listener');
      unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      console.log('AuthProvider - Attempting sign in');
      const userCredential = await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
      console.log('AuthProvider - Sign in successful:', userCredential.user);
      
      // Save the admin UID to Firestore
      try {
        console.log('AuthProvider - Attempting to save admin data to Firestore');
        
        // Create a document in the admins collection with the user's UID as the document ID
        const adminDocRef = doc(getFirebaseDb(), 'admins', userCredential.user.uid);
        
        // Get existing admin data to preserve the role
        const adminDoc = await getDoc(adminDocRef);
        const existingData = adminDoc.exists() ? adminDoc.data() : {};
        const existingRole = existingData.role as AdminRole;
        
        // Set the document data with uid and email, preserving existing role
        await setDoc(adminDocRef, {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          timestamp: new Date(),
          role: existingRole || 'editor' // Preserve existing role or set default to editor
        }, { merge: true });
        
        // Set the role in the context and localStorage
        setRole(existingRole || 'editor');
        localStorage.setItem('userRole', existingRole || 'editor');
        
        console.log('AuthProvider - Admin document created/updated successfully');
      } catch (firestoreError) {
        console.error('AuthProvider - Firestore error:', firestoreError);
        // Continue with the sign-in process even if Firestore update fails
        setRole('editor');
        localStorage.setItem('userRole', 'editor');
      }
      
      setUser(userCredential.user);
    } catch (error) {
      console.error('AuthProvider - Sign in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      console.log('AuthProvider - Attempting sign out');
      await firebaseSignOut(getFirebaseAuth());
      console.log('AuthProvider - Sign out successful');
      
      // Clear all localStorage items
      localStorage.removeItem('token');
      localStorage.removeItem('gameBaseUrl');
      localStorage.removeItem('adminBaseUrl');
      localStorage.removeItem('language');
      localStorage.removeItem('userRole');
      
      setUser(null);
      setRole(null);
    } catch (error) {
      console.error('AuthProvider - Sign out error:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    role,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 